import { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

const getFallbackLabel = (value) => {
  if (!value) return 'DW';

  const words = value.trim().split(/\s+/).filter(Boolean);

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return `${words[0][0] || ''}${words[1][0] || ''}`.toUpperCase();
};

const BrandLogo = ({ src, alt, maxWidth = 220, maxHeight = 64 }) => {
  const [broken, setBroken] = useState(false);
  const fallbackLabel = getFallbackLabel(alt);

  if (!src || broken) {
    return (
      <Box
        sx={{
          width: { xs: 72, sm: 92 },
          height: { xs: 72, sm: 92 },
          borderRadius: 3,
          display: 'grid',
          placeItems: 'center',
          border: '1px solid rgba(255,255,255,0.18)',
          bgcolor: 'rgba(255,255,255,0.14)',
          color: '#ffffff',
          fontSize: { xs: '1.4rem', sm: '1.8rem' },
          fontWeight: 800,
          letterSpacing: '-0.04em',
          flexShrink: 0,
        }}
      >
        {fallbackLabel}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onError={() => setBroken(true)}
      sx={{
        maxWidth,
        maxHeight,
        width: '100%',
        objectFit: 'contain',
        display: 'block',
      }}
    />
  );
};

const HighlightCard = ({ label, value }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 3,
      bgcolor: 'rgba(255,255,255,0.12)',
      color: '#ffffff',
      backdropFilter: 'blur(6px)',
    }}
  >
    <Typography
      sx={{
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: '0.14em',
        opacity: 0.74,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ mt: 0.75, fontWeight: 700, lineHeight: 1.35 }}>
      {value}
    </Typography>
  </Paper>
);

export const PasswordResetLoader = ({ message = 'Loading secure access...' }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        background: `radial-gradient(circle at top left, ${alpha(theme.palette.secondary.main, 0.18)} 0%, transparent 32%),
          radial-gradient(circle at bottom right, ${alpha(theme.palette.primary.main, 0.22)} 0%, transparent 38%),
          linear-gradient(135deg, #f7fafc 0%, #eef4fb 48%, #ffffff 100%)`,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 520,
          p: { xs: 3, sm: 4 },
          borderRadius: 5,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          boxShadow: `0 28px 70px ${alpha(theme.palette.primary.main, 0.12)}`,
          background: alpha('#ffffff', 0.86),
          backdropFilter: 'blur(16px)',
        }}
      >
        <Stack spacing={2.5} alignItems="center">
          <CircularProgress size={40} />
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, letterSpacing: '-0.03em', textAlign: 'center' }}
          >
            {message}
          </Typography>
          <Box sx={{ width: '100%' }}>
            <LinearProgress
              sx={{
                height: 10,
                borderRadius: 999,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                },
              }}
            />
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

const PasswordResetShell = ({
  logoSrc,
  logoBackground,
  logoAlt,
  heroBadge,
  heroEyebrow,
  heroTitle,
  heroDescription,
  heroHighlights = [],
  heroMediaSrc,
  heroMediaAlt,
  heroMediaFallback,
  panelBadge,
  panelTitle,
  panelDescription,
  footerHref,
  footerLabel,
  children,
}) => {
  const theme = useTheme();
  const [mediaBroken, setMediaBroken] = useState(false);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: `radial-gradient(circle at top left, ${alpha(theme.palette.secondary.main, 0.22)} 0%, transparent 34%),
          radial-gradient(circle at bottom right, ${alpha(theme.palette.primary.main, 0.24)} 0%, transparent 40%),
          linear-gradient(135deg, #f7fafc 0%, #eef4fb 46%, #ffffff 100%)`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.5,
          backgroundImage: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 1px, transparent 1px)`,
          backgroundSize: '38px 38px',
          maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.7), transparent 92%)',
        }}
      />
      <Box
        sx={{
          position: 'relative',
          maxWidth: 1180,
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 3, sm: 4, md: 6 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            overflow: 'hidden',
            borderRadius: { xs: 4, md: 6 },
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            boxShadow: `0 30px 90px ${alpha(theme.palette.primary.main, 0.12)}`,
            backdropFilter: 'blur(18px)',
            background: alpha('#ffffff', 0.82),
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.08fr) minmax(360px, 0.92fr)' },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              px: { xs: 3, sm: 4, md: 5 },
              py: { xs: 4, md: 5 },
              color: '#ffffff',
              background: `linear-gradient(160deg, ${theme.palette.primary.dark || theme.palette.primary.main} 0%, ${theme.palette.primary.main} 48%, ${theme.palette.secondary.main} 100%)`,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -80,
                right: -40,
                width: 220,
                height: 220,
                borderRadius: '50%',
                bgcolor: alpha('#ffffff', 0.12),
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -120,
                left: -70,
                width: 260,
                height: 260,
                borderRadius: '50%',
                bgcolor: alpha('#ffffff', 0.1),
              }}
            />
            <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
              {heroBadge ? (
                <Chip
                  label={heroBadge}
                  sx={{
                    width: 'fit-content',
                    borderRadius: 999,
                    bgcolor: alpha('#ffffff', 0.16),
                    color: '#ffffff',
                    fontWeight: 700,
                  }}
                />
              ) : null}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                }}
              >
                <Box>
                  {heroEyebrow ? (
                    <Typography
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: '0.18em',
                        fontSize: 12,
                        opacity: 0.78,
                      }}
                    >
                      {heroEyebrow}
                    </Typography>
                  ) : null}
                  <Typography
                    sx={{
                      mt: heroEyebrow ? 1 : 0,
                      fontSize: { xs: '2.2rem', md: '3.2rem' },
                      lineHeight: 0.98,
                      fontWeight: 800,
                      letterSpacing: '-0.04em',
                      maxWidth: 560,
                    }}
                  >
                    {heroTitle}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    bgcolor: logoBackground || alpha('#ffffff', 0.12),
                    backdropFilter: 'blur(6px)',
                    maxWidth: { xs: '100%', sm: 240 },
                  }}
                >
                  <BrandLogo
                    src={logoSrc}
                    alt={logoAlt}
                    maxWidth={220}
                    maxHeight={72}
                  />
                </Box>
              </Box>
              {heroDescription ? (
                <Typography
                  sx={{
                    maxWidth: 640,
                    fontSize: { xs: '1rem', md: '1.08rem' },
                    color: alpha('#ffffff', 0.9),
                  }}
                >
                  {heroDescription}
                </Typography>
              ) : null}
              {heroHighlights.length ? (
                <Stack spacing={1.5}>
                  {heroHighlights.map((item) => (
                    <HighlightCard
                      key={`${item.label}-${item.value}`}
                      label={item.label}
                      value={item.value}
                    />
                  ))}
                </Stack>
              ) : null}
              {heroMediaSrc && !mediaBroken ? (
                <Paper
                  elevation={0}
                  sx={{
                    overflow: 'hidden',
                    borderRadius: 4,
                    bgcolor: alpha('#ffffff', 0.12),
                    border: `1px solid ${alpha('#ffffff', 0.16)}`,
                  }}
                >
                  <Box
                    component="img"
                    src={heroMediaSrc}
                    alt={heroMediaAlt}
                    onError={() => setMediaBroken(true)}
                    sx={{
                      display: 'block',
                      width: '100%',
                      minHeight: 220,
                      objectFit: 'cover',
                    }}
                  />
                </Paper>
              ) : heroMediaFallback ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 4,
                    bgcolor: alpha('#ffffff', 0.12),
                    border: `1px solid ${alpha('#ffffff', 0.16)}`,
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>{heroMediaFallback}</Typography>
                </Paper>
              ) : null}
            </Stack>
          </Box>
          <Box
            sx={{
              px: { xs: 3, sm: 4, md: 5 },
              py: { xs: 4, md: 5 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 3,
            }}
          >
            {panelBadge ? (
              <Chip
                label={panelBadge}
                sx={{
                  width: 'fit-content',
                  borderRadius: 999,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                }}
              />
            ) : null}
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
                {panelTitle}
              </Typography>
              {panelDescription ? (
                <Typography sx={{ mt: 1.25, color: 'text.secondary', lineHeight: 1.7 }}>
                  {panelDescription}
                </Typography>
              ) : null}
            </Box>
            {children}
          </Box>
          <Box
            component="footer"
            sx={{
              gridColumn: '1 / -1',
              borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              px: { xs: 3, md: 4 },
              py: 2.5,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              background: alpha('#ffffff', 0.82),
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              {footerHref ? (
                <Link
                  href={footerHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  sx={{ color: 'inherit' }}
                >
                  <Typography sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
                    {footerLabel}
                  </Typography>
                </Link>
              ) : (
                <Typography sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
                  {footerLabel}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {`© ${new Date().getFullYear()} ${footerLabel}. All rights reserved.`}
              </Typography>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default PasswordResetShell;
